export interface AuthData {
  user: any;
  access_token: string;
  timestamp?: number;
}

const AUTH_STORAGE_KEY = 'habita4u_auth_data';
const AUTH_SESSION_STORAGE_KEY = 'habita4u_auth_session_data';
export const AUTH_STATE_CHANGE_EVENT = 'habita4u:auth-state-change';

let inMemoryAuthData: AuthData | null = null;

type AuthStateChangeDetail = {
  authenticated: boolean;
};

function decodeBase64Url(value: string): string | null {
  const normalizedValue = value.replace(/-/g, '+').replace(/_/g, '/');
  const paddingLength = (4 - (normalizedValue.length % 4)) % 4;
  const paddedValue = `${normalizedValue}${'='.repeat(paddingLength)}`;

  try {
    return globalThis.atob(paddedValue);
  } catch {
    return null;
  }
}

function extractJwtExpiration(accessToken: string): number | null {
  const [, payload] = accessToken.split('.');
  if (!payload) return null;

  const decodedPayload = decodeBase64Url(payload);
  if (!decodedPayload) return null;

  try {
    const claims = JSON.parse(decodedPayload) as Record<string, unknown>;
    const expiresAt = Number(claims.exp);

    return Number.isFinite(expiresAt) ? expiresAt : null;
  } catch {
    return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function extractUserFromAuthData(value: unknown): unknown {
  if (!isRecord(value)) return null;

  const record = value;
  const data = isRecord(record.data) ? record.data : {};
  const session = isRecord(record.session) ? record.session : {};
  const dataSession = isRecord(data.session) ? data.session : {};
  const candidates = [
    record.user,
    data.user,
    session.user,
    dataSession.user,
    record.app_metadata || record.id || record.email ? record : null,
  ];

  return candidates.find(isRecord) ?? null;
}

function resolveAuthUserRecord(value: unknown): Record<string, unknown> {
  const record = isRecord(value) ? value : {};
  const data = isRecord(record.data) ? record.data : {};
  const session = isRecord(record.session) ? record.session : {};
  const dataSession = isRecord(data.session) ? data.session : {};

  const candidates = [
    record.user,
    data.user,
    session.user,
    dataSession.user,
    record,
  ];

  return candidates.map((candidate) => isRecord(candidate) ? candidate : null).find(Boolean) ?? {};
}

function normalizeProfileValue(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }

  if (typeof value === 'string' && value.trim() !== '') {
    return value.split(',').map((item) => item.trim()).filter(Boolean);
  }

  return [];
}

export function normalizeProfileKey(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .trim();
}

export function collectAuthProfiles(value: unknown): string[] {
  const userRecord = resolveAuthUserRecord(value);
  const appMetadata = isRecord(userRecord.app_metadata) ? userRecord.app_metadata : {};

  return [
    ...normalizeProfileValue(userRecord.profiles),
    ...normalizeProfileValue(userRecord.profile),
    ...normalizeProfileValue(userRecord.perfil),
    ...normalizeProfileValue(userRecord.role),
    ...normalizeProfileValue(appMetadata.profiles),
    ...normalizeProfileValue(appMetadata.profile),
    ...normalizeProfileValue(appMetadata.perfil),
    ...normalizeProfileValue(appMetadata.role),
  ];
}

function firstString(...values: unknown[]): string {
  const value = values.find((item) => (
    item !== null &&
    item !== undefined &&
    String(item).trim() !== ''
  ));

  return value === undefined || value === null ? '' : String(value).trim();
}

function firstIdentityDataEmail(userRecord: Record<string, unknown>): string {
  if (!Array.isArray(userRecord.identities)) return '';

  for (const identity of userRecord.identities) {
    const identityRecord = isRecord(identity) ? identity : {};
    const identityData = isRecord(identityRecord.identity_data) ? identityRecord.identity_data : {};
    const email = firstString(identityRecord.email, identityData.email);
    if (email) return email;
  }

  return '';
}

export class AuthStorage {
  private static readonly STORAGE_KEY = AUTH_SESSION_STORAGE_KEY;
  private static readonly PERSISTENT_STORAGE_KEY = AUTH_STORAGE_KEY;
  private static persistentStorageDisabled = false;
  private static storageSyncListenerRegistered = false;

  private static hasValidAccessToken(authData: AuthData | null): authData is AuthData {
    const accessToken = authData?.access_token;
    if (typeof accessToken !== 'string' || accessToken.trim() === '') return false;
    if (accessToken.split('.').length !== 3) return false;

    const expiresAt = Number((authData as AuthData & { expires_at?: unknown })?.expires_at);
    if (Number.isFinite(expiresAt)) return expiresAt * 1000 > Date.now();

    const jwtExpiresAt = extractJwtExpiration(accessToken);
    if (jwtExpiresAt === null) return false;
    if (jwtExpiresAt * 1000 <= Date.now()) return false;

    return true;
  }

  private static parseAuthData(value: string | null): AuthData | null {
    if (!value) return null;

    try {
      return JSON.parse(value) as AuthData;
    } catch {
      return null;
    }
  }

  private static storageAvailable(): boolean {
    try {
      return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
    } catch {
      return false;
    }
  }

  private static persistentStorageAvailable(): boolean {
    if (this.persistentStorageDisabled) return false;

    try {
      return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
    } catch {
      this.persistentStorageDisabled = true;
      return false;
    }
  }

  private static getPersistentAuthData(): AuthData | null {
    if (!this.persistentStorageAvailable()) return null;

    try {
      return this.parseAuthData(window.localStorage.getItem(this.PERSISTENT_STORAGE_KEY));
    } catch {
      this.persistentStorageDisabled = true;
      return null;
    }
  }

  private static getSessionAuthData(): AuthData | null {
    if (!this.storageAvailable()) return null;

    try {
      return this.parseAuthData(window.sessionStorage.getItem(this.STORAGE_KEY));
    } catch {
      return null;
    }
  }

  private static syncSessionAuthData(authData: AuthData): void {
    try {
      if (this.storageAvailable()) {
        window.sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(authData));
      }
    } catch {
      // Mantém somente em memória quando sessionStorage não estiver disponível.
    }
  }

  private static persistAuthData(authData: AuthData): boolean {
    if (!this.persistentStorageAvailable()) return false;

    try {
      window.localStorage.setItem(this.PERSISTENT_STORAGE_KEY, JSON.stringify(authData));
      return true;
    } catch {
      this.persistentStorageDisabled = true;
      // Mantém somente na sessão da aba quando localStorage não estiver disponível.
      return false;
    }
  }

  private static clearPersistentAuthData(): void {
    try {
      if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') return;
      window.localStorage.removeItem(this.PERSISTENT_STORAGE_KEY);
    } catch {
      this.persistentStorageDisabled = true;
    }
  }

  private static clearSessionAuthData(): void {
    try {
      if (this.storageAvailable()) {
        window.sessionStorage.removeItem(this.STORAGE_KEY);
      }
    } catch {
      // Sem ação: a limpeza em memória/localStorage ainda mantém o logout consistente.
    }
  }

  private static ensureStorageSyncListener(): void {
    if (
      this.storageSyncListenerRegistered ||
      typeof window === 'undefined' ||
      typeof window.addEventListener !== 'function'
    ) {
      return;
    }

    window.addEventListener('storage', (event) => {
      if (event.key !== this.PERSISTENT_STORAGE_KEY) return;

      const nextAuthData = this.parseAuthData(event.newValue);
      if (this.hasValidAccessToken(nextAuthData)) {
        inMemoryAuthData = nextAuthData;
        this.syncSessionAuthData(nextAuthData);
        this.notifyAuthStateChange(true);
        return;
      }

      inMemoryAuthData = null;
      this.clearSessionAuthData();
      this.notifyAuthStateChange(false);
    });
    this.storageSyncListenerRegistered = true;
  }

  private static notifyAuthStateChange(authenticated: boolean): void {
    if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;

    window.dispatchEvent(new CustomEvent<AuthStateChangeDetail>(AUTH_STATE_CHANGE_EVENT, {
      detail: { authenticated },
    }));
  }

  /**
   * Salva as informações de autenticação na sessão atual e entre abas do mesmo domínio.
   */
  static saveAuthData(authData: AuthData | any): void {
    this.ensureStorageSyncListener();
    inMemoryAuthData = authData;

    try {
      this.syncSessionAuthData(authData);
      this.persistAuthData(authData);
      this.notifyAuthStateChange(this.hasValidAccessToken(authData));
    } catch (error) {
      console.error('Erro ao salvar dados de autenticação:', error);
    }
  }

  /**
   * Recupera as informações de autenticação da sessão atual.
   */
  static getAuthData(): AuthData | null {
    this.ensureStorageSyncListener();

    if (inMemoryAuthData) {
      if (!this.hasValidAccessToken(inMemoryAuthData)) {
        inMemoryAuthData = null;
      } else {
        const isPersistentStorageAvailable = this.persistentStorageAvailable();
        const persistentAuthData = isPersistentStorageAvailable ? this.getPersistentAuthData() : null;
        const shouldUsePersistentStorage = isPersistentStorageAvailable && this.persistentStorageAvailable();

        if (shouldUsePersistentStorage) {
          if (!this.hasValidAccessToken(persistentAuthData)) {
            this.clearAuthData();
            return null;
          }

          if (persistentAuthData.access_token !== inMemoryAuthData.access_token) {
            inMemoryAuthData = persistentAuthData;
            this.syncSessionAuthData(persistentAuthData);

            return persistentAuthData;
          }
        }

        return inMemoryAuthData;
      }
    }

    try {
      const sessionAuthData = this.getSessionAuthData();
      const isPersistentStorageAvailable = this.persistentStorageAvailable();
      const persistentAuthData = isPersistentStorageAvailable ? this.getPersistentAuthData() : null;
      const shouldUsePersistentStorage = isPersistentStorageAvailable && this.persistentStorageAvailable();
      const validSessionAuthData = this.hasValidAccessToken(sessionAuthData) ? sessionAuthData : null;
      const validPersistentAuthData = this.hasValidAccessToken(persistentAuthData) ? persistentAuthData : null;

      if (shouldUsePersistentStorage) {
        if (!validPersistentAuthData) {
          if (sessionAuthData || persistentAuthData) {
            this.clearAuthData();
          }
          return null;
        }

        if (!validSessionAuthData || validSessionAuthData.access_token !== validPersistentAuthData.access_token) {
          this.syncSessionAuthData(validPersistentAuthData);
        }

        inMemoryAuthData = validPersistentAuthData;

        return validPersistentAuthData;
      }

      if (!validSessionAuthData) {
        if (sessionAuthData) {
          this.clearAuthData();
        }
        return null;
      }

      inMemoryAuthData = validSessionAuthData;

      return validSessionAuthData;
    } catch (error) {
      console.error('Erro ao recuperar dados de autenticação:', error);
      this.clearAuthData();
      return null;
    }
  }

  /**
   * Verifica se o usuário está autenticado
   */
  static isAuthenticated(): boolean {
    const authData = this.getAuthData();
    return this.hasValidAccessToken(authData);
  }

  /**
   * Obtém o token de acesso
   */
  static getAccessToken(): string | null {
    const authData = this.getAuthData();
    return this.hasValidAccessToken(authData) ? authData?.access_token ?? null : null;
  }

  /**
   * Obtém os dados do usuário
   */
  static getUser(): any | null {
    const authData = this.getAuthData();
    return extractUserFromAuthData(authData);
  }

  /**
   * Remove as informações de autenticação da sessão atual.
   */
  static clearAuthData(): void {
    this.ensureStorageSyncListener();
    inMemoryAuthData = null;

    try {
      this.clearSessionAuthData();
      this.clearPersistentAuthData();
    } catch (error) {
      console.error('Erro ao remover dados de autenticação:', error);
    } finally {
      this.notifyAuthStateChange(false);
    }
  }

}

/**
 * Funções de conveniência para uso direto
 */
export const saveAuthData = (authData: AuthData | any): void => AuthStorage.saveAuthData(authData);
export const isAuthenticated = (): boolean => AuthStorage.isAuthenticated();
export const getAccessToken = (): string | null => AuthStorage.getAccessToken();
export const getUser = (): any | null => AuthStorage.getUser();
export const getAuthProfiles = (user: unknown = AuthStorage.getUser()): string[] => (
  Array.from(new Set(
    collectAuthProfiles(user)
      .map(normalizeProfileKey)
      .filter((profile) => profile && !['ANON', 'AUTHENTICATED'].includes(profile))
  ))
);
export const isAdminUser = (user: unknown = AuthStorage.getUser()): boolean => (
  getAuthProfiles(user).includes('ADMIN')
);
export const canManageUsers = (user: unknown = AuthStorage.getUser()): boolean => (
  getAuthProfiles(user).some((profile) => ['ADMIN', 'GERENTE', 'SUPERVISOR', 'GESTOR'].includes(profile))
);
export const isSdrUser = (user: unknown = AuthStorage.getUser()): boolean => (
  getAuthProfiles(user).includes('SDR')
);
const SDR_SCOPE_OVERRIDE_PROFILES = ['ADMIN', 'GESTOR', 'GERENTE', 'SUPERVISOR'];
export const isSdrScopedUser = (user: unknown = AuthStorage.getUser()): boolean => {
  const profiles = getAuthProfiles(user);
  return profiles.includes('SDR') && !profiles.some((profile) => SDR_SCOPE_OVERRIDE_PROFILES.includes(profile));
};
export const getAuthenticatedUserEmail = (user: unknown = AuthStorage.getUser()): string => {
  const userRecord = resolveAuthUserRecord(user);
  const appMetadata = isRecord(userRecord.app_metadata) ? userRecord.app_metadata : {};

  return firstString(
    userRecord.email,
    userRecord.user_email,
    userRecord.responsible_email,
    appMetadata.email,
    appMetadata.user_email,
    appMetadata.responsible_email,
    firstIdentityDataEmail(userRecord)
  );
};
export const getSdrResponsibleEmail = (user: unknown = AuthStorage.getUser()): string | null => (
  isSdrScopedUser(user) ? getAuthenticatedUserEmail(user) || null : null
);
export const clearAuthData = (): void => AuthStorage.clearAuthData();
